import { BdExportAsFileInput } from './export-input';

import { BdExportAsFileDialogFormHubspotContact } from './dialog/hubspot-contact/hubspot-contact';
import { from, Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { BdHubspotFormService } from '../hubspot/forms/hubspot-form.service';
import {
  BdExportAsFileDataSheet,
  BdLocaleHelper,
} from '@bindoc-projects/util-domain-lib';
import { BdExportAsFileCsvConverter } from './converter/csv/csv.converter';
import { BdExportAsFileExcelConverter } from './converter/excel/excel.converter';
import {
  BdExportAsFileAuthenticationStrategyContactConfig,
  BdExportAsFileAuthenticationStrategyReduceConfig,
  BdExportAsFileConfig,
} from './export.config';
import { BdExportAsFileConverter } from './converter/export.converter';
import { UserFileService } from '@bindoc-projects/user-lib/data';

export class BdExportAsFileExportHandler {
  public isValid;

  constructor(
    private readonly _hubspotFormService: BdHubspotFormService,
    private _input: BdExportAsFileInput,
    private _config: BdExportAsFileConfig,
    private _authenticated: boolean,
    private readonly _fileService?: UserFileService,
    private _contact?: BdExportAsFileDialogFormHubspotContact,
  ) {
    this._updateIsValid();
  }

  public export(): Observable<{ result?: Blob; errors?: string[] }> {
    let exportData: Observable<
      BdExportAsFileDataSheet | BdExportAsFileDataSheet[]
    >;

    if (!this._authenticated) {
      if (this._config.authenticationStrategy === 'contact') {
        exportData = this._exportContact();
      } else if (this._config.authenticationStrategy === 'reduce') {
        exportData = this._exportReduced();
      }
      this._input.saveInDownloadCenter = false;
    } else {
      exportData = from(this._input.data.getExport());
    }

    // No file service available in this context (it's optional) — checking
    // "Im File-Center ablegen" must not be able to reach `.upload()` on it.
    if (!this._fileService) {
      this._input.saveInDownloadCenter = false;
    }

    let processorData = exportData.pipe(
      switchMap((exportData) => {
        const converter = this._getExportConverter(exportData);

        return from(converter.process());
      }),
    );

    const fileService = this._fileService;
    if (this._input.saveInDownloadCenter && fileService) {
      processorData = processorData.pipe(
        switchMap((resultData) => {
          // A failed conversion has `errors` and no `result` — never upload
          // that (it would stringify to a literal "undefined" file, BDA-5941).
          // Let it pass through unchanged so the dialog still reports the error.
          if (resultData.errors || !resultData.result) {
            return of(resultData);
          }

          const file = new File(
            [resultData.result],
            `${this._input.filename}.${this._input.exportType}`,
            { type: resultData.result.type },
          );

          return fileService
            .upload(file, this._input.labels || [])
            .pipe(map(() => resultData));
        }),
      );
    }

    return processorData;
  }

  private _exportContact(): Observable<
    BdExportAsFileDataSheet | BdExportAsFileDataSheet[]
  > {
    const contactConfig = this.config
      .authenticationStrategyConfig as BdExportAsFileAuthenticationStrategyContactConfig;

    return this._hubspotFormService
      .sendForm(
        contactConfig.portalId,
        contactConfig.formId,
        this._contact?.toRequestBody(),
        this._contact?.data.privacyConsent,
        window.location.href,
        `Export ${this._input.filename}`,
      )
      .pipe(
        switchMap(() => {
          return from(this._input.data.getExport());
        }),
      );
  }

  private _exportReduced(): Observable<BdExportAsFileDataSheet> {
    return from(this._input.data.getExport()).pipe(
      map((exportData) => {
        const resultSetCount = (
          this.config
            .authenticationStrategyConfig as BdExportAsFileAuthenticationStrategyReduceConfig
        ).resultSetCount;
        const reducedExportData: BdExportAsFileDataSheet = Array.isArray(
          exportData,
        )
          ? {
              ...exportData[0],
              data: exportData[0].data.slice(0, resultSetCount),
            }
          : {
              ...exportData,
              data: exportData.data.slice(0, resultSetCount),
            };

        return reducedExportData;
      }),
    );
  }

  private _getExportConverter(
    exportData: BdExportAsFileDataSheet | BdExportAsFileDataSheet[],
  ): BdExportAsFileConverter {
    const userPreferredLocale = BdLocaleHelper.getPreferredLocale();

    let converter: BdExportAsFileConverter;

    if (this._input.exportType === 'csv') {
      // A CSV file holds exactly one table, so a multi-sheet export writes its
      // FIRST sheet — the primary one every producer puts first — instead of
      // failing (BDA-5923). It used to throw, which surfaced as
      // "Export fehlgeschlagen": an export offering both formats was then
      // broken in one of them, and the honest reading is a format limitation,
      // not an error. `_exportReduced` above already collapses to
      // `exportData[0]` for the same reason. Additional sheets — the Klinik-
      // Cube's "Datenvollständigkeit", a market export's per-year tables — are
      // therefore an xlsx-only artifact; a trigger for which they are
      // essential should offer xlsx alone.
      converter = new BdExportAsFileCsvConverter(
        Promise.resolve(Array.isArray(exportData) ? exportData[0] : exportData),
        userPreferredLocale,
        this._input.filename,
      );
    } else if (this._input.exportType === 'xlsx') {
      converter = new BdExportAsFileExcelConverter(
        Promise.resolve(exportData),
        userPreferredLocale,
        this._input.filename,
      );
    }

    return converter;
  }

  private _updateIsValid(): void {
    let isValid = true;

    if (
      !this._exportInputIsValid() ||
      (!this.authenticated &&
        this._config.authenticationStrategy === 'contact' &&
        (!this._contact || !this._contact.dataIsValid()))
    ) {
      isValid = false;
    }

    this.isValid = isValid;
  }

  private _exportInputIsValid(): boolean {
    return (
      !!this._input.exportType && !!this._input.data && !!this._input.filename
    );
  }

  get input(): BdExportAsFileInput {
    return this._input;
  }

  set input(value: BdExportAsFileInput) {
    this._input = value;
    this._updateIsValid();
  }

  get config(): BdExportAsFileConfig {
    return this._config;
  }

  set config(value: BdExportAsFileConfig) {
    this._config = value;
    this._updateIsValid();
  }

  get authenticated(): boolean {
    return this._authenticated;
  }

  set authenticated(value: boolean) {
    this._authenticated = value;
    this._updateIsValid();
  }

  get contact(): BdExportAsFileDialogFormHubspotContact {
    return this._contact;
  }

  set contact(value: BdExportAsFileDialogFormHubspotContact) {
    this._contact = value;
    this._updateIsValid();
  }
}
